import { useEffect } from "react";
import { X } from "@phosphor-icons/react";

export function PageHeader({ overline, title, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 animate-fade-up">
      <div>
        {overline && <div className="overline mb-1">{overline}</div>}
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{title}</h1>
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`bg-card border border-border rounded-md ${className}`} {...rest}>{children}</div>;
}

export function StatCard({ label, value, icon: Icon, tone = "default" }) {
  const tones = {
    default: "text-foreground",
    orange: "text-primary",
    green: "text-success",
    red: "text-danger",
  };
  return (
    <Card className="p-6 hover:-translate-y-0.5 hover:shadow-md transition-transform duration-200">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        {Icon && <Icon size={20} weight="bold" className="text-muted-foreground" />}
      </div>
      <div className={`font-heading font-extrabold text-3xl mt-3 tracking-tight ${tones[tone]}`}>{value}</div>
    </Card>
  );
}

export function Badge({ status }) {
  const map = {
    activo: "bg-blue-50 text-blue-700",
    pendiente: "bg-amber-50 text-amber-700",
    aprobada: "bg-emerald-50 text-emerald-700",
    pagada: "bg-emerald-50 text-emerald-700",
    pagado: "bg-emerald-50 text-emerald-700",
    rechazada: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-flex px-2.5 py-1 rounded-sm text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}

export function Modal({ open, onClose, title, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-lg animate-fade-up">
        <div className="flex items-center justify-between px-6 h-14 border-b border-border sticky top-0 bg-card">
          <h3 className="font-heading font-bold text-lg">{title}</h3>
          <button onClick={onClose} data-testid="modal-close-btn" className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export const inputClass =
  "w-full h-11 px-3 rounded-sm border border-input bg-white focus:ring-2 focus:ring-primary outline-none text-sm";

export function EmptyState({ icon: Icon, title, subtitle, action }) {
  return (
    <Card className="p-12 text-center">
      {Icon && <Icon size={40} weight="light" className="mx-auto text-muted-foreground mb-3" />}
      <div className="font-heading font-bold text-lg">{title}</div>
      {subtitle && <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{subtitle}</p>}
      {action && <div className="mt-4">{action}</div>}
    </Card>
  );
}

export function PrimaryButton({ children, ...props }) {
  return (
    <button {...props}
      className="inline-flex items-center gap-2 h-11 px-5 bg-primary text-white font-semibold rounded-sm hover:bg-orange-600 transition-colors duration-200 disabled:opacity-60">
      {children}
    </button>
  );
}
